import React, { useMemo, useRef } from 'react';
import { getZoomTransform } from '../../../utils/zoomPosition';
import {
  CUSTOM_DEFAULT_ZOOM_ORIGIN,
  CUSTOM_INTERPOLATION_INITIAL_ZOOM,
  CUSTOM_ZOOM_BOUNDS,
} from '../../../constants/games/zoomedInCustomGame';

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const toSafeOrigin = (value) => {
  const x = Number(value?.x);
  const y = Number(value?.y);
  return {
    x: Number.isFinite(x) ? clamp01(x) : CUSTOM_DEFAULT_ZOOM_ORIGIN.x,
    y: Number.isFinite(y) ? clamp01(y) : CUSTOM_DEFAULT_ZOOM_ORIGIN.y,
  };
};

const CustomZoomOriginPicker = ({
  imageUrl,
  value,
  onChange,
  zoomLevel = CUSTOM_INTERPOLATION_INITIAL_ZOOM,
  zoomBounds = CUSTOM_ZOOM_BOUNDS,
  disabled = false,
}) => {
  const canvasRef = useRef(null);
  const origin = toSafeOrigin(value);

  const zoomTransform = useMemo(
    () => getZoomTransform(origin, zoomLevel, zoomBounds),
    [origin, zoomLevel, zoomBounds],
  );

  const updateOriginFromPoint = (clientX, clientY) => {
    if (disabled || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    const x = clamp01((clientX - rect.left) / rect.width);
    const y = clamp01((clientY - rect.top) / rect.height);
    onChange?.({ x, y });
  };

  const onPointerDown = (event) => {
    updateOriginFromPoint(event.clientX, event.clientY);
  };

  const onPointerMove = (event) => {
    if (!event.buttons) return;
    updateOriginFromPoint(event.clientX, event.clientY);
  };

  if (!imageUrl) {
    return <div className="custom-zoom-picker__empty">Upload an image to set custom zoom start.</div>;
  }

  return (
    <div className="custom-zoom-picker">
      <div
        ref={canvasRef}
        className={`custom-zoom-picker__canvas${disabled ? ' is-disabled' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const rect = canvasRef.current?.getBoundingClientRect?.();
            if (!rect) return;
            updateOriginFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
          }
        }}
        aria-label="Set zoom start point"
      >
        <img src={imageUrl} alt="Zoom origin selector" className="custom-zoom-picker__base-image" />
        <span
          className="custom-zoom-picker__marker"
          style={{
            left: `${origin.x * 100}%`,
            top: `${origin.y * 100}%`,
          }}
        />
      </div>

      <div className="custom-zoom-picker__preview">
        <img
          src={imageUrl}
          alt="Zoomed start preview"
          className="custom-zoom-picker__preview-image"
          style={{
            width: `${zoomLevel}%`,
            height: `${zoomLevel}%`,
            transform: zoomTransform,
          }}
        />
      </div>
    </div>
  );
};

export default CustomZoomOriginPicker;
